/**
 * AP-32 5.8 — **Die Ablageleiste.** Sichtbar, sobald die Ablage etwas enthält,
 * nicht eingeklappt: Wer eine Zeile markiert hat und Strg+C drückt, legt eine
 * Belegzeile ab — die Leiste zeigt, dass es passiert ist.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClipboardList, ImageIcon, X } from 'lucide-react'
import { formatBetrag } from '../lib/grid'
import type { ClipboardItem } from '../types/document.types'

interface Props {
  items: ClipboardItem[]
  waehrung: string
  kannEinfuegen: boolean
  preise: boolean
  onPaste: (itemIds: string[], refreshPrices: boolean) => void
  onRemove: (itemId: string) => void
  onClear: () => void
}

export function ClipboardBar({ items, waehrung, kannEinfuegen, preise, onPaste, onRemove, onClear }: Props) {
  const { t } = useTranslation()
  const [offen, setOffen] = useState(true)
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [frisch, setFrisch] = useState(false)

  useEffect(() => setAuswahl(new Set(items.map((i) => i.id))), [items])

  if (!items.length) return null

  return (
    // Eingefügt wird hinter der markierten Zeile — ein Klick hier hebt die Markierung nicht auf.
    <div className="rounded-md border border-primary/40 bg-primary/5" data-auswahl-behalten>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
        <button type="button" onClick={() => setOffen((v) => !v)} className="flex items-center gap-2 font-medium">
          <ClipboardList className="h-4 w-4" />
          {t('documents.clipboard.title', { count: items.length })}
        </button>
        <span className="flex-1" />
        {preise && (
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={frisch} onChange={(e) => setFrisch(e.target.checked)} />
            {t('documents.clipboard.refreshPrices')}
          </label>
        )}
        <button
          type="button"
          disabled={!kannEinfuegen || !auswahl.size}
          onClick={() => onPaste([...auswahl], frisch)}
          className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
        >
          {t('documents.clipboard.paste')}
        </button>
        <button type="button" onClick={onClear} className="rounded-md border border-input px-2.5 py-1 text-xs hover:bg-muted/50">
          {t('documents.clipboard.clear')}
        </button>
      </div>
      {offen && (
        <ul className="border-t border-primary/20 px-3 py-1.5 text-sm">
          {items.map((i) => (
            <li key={i.id} className="flex items-center gap-2 py-0.5">
              <input
                type="checkbox"
                checked={auswahl.has(i.id)}
                onChange={() =>
                  setAuswahl((v) => {
                    const neu = new Set(v)
                    if (neu.has(i.id)) neu.delete(i.id)
                    else neu.add(i.id)
                    return neu
                  })
                }
              />
              <span className="w-40 truncate text-xs text-muted-foreground">
                {i.sourceLabel ?? t(`documents.clipboard.source.${i.sourceKind}`)}
              </span>
              <span className="flex-1 truncate">
                {i.position.articleName || i.position.description || '—'}
                {i.position.children?.length ? (
                  <span className="ml-1 text-xs text-muted-foreground">
                    {t('documents.clipboard.withChildren', { count: i.position.children.length })}
                  </span>
                ) : null}
                {i.detached && <span className="ml-1 text-xs text-amber-600">{t('documents.clipboard.detachedBadge')}</span>}
              </span>
              {i.hasImage && <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />}
              {i.amount !== undefined && <span className="w-24 text-right tabular-nums">{formatBetrag(i.amount, waehrung)}</span>}
              <button type="button" onClick={() => onRemove(i.id)} className="text-muted-foreground hover:text-destructive">
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
